import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';

const PENDING_INVITE_KEY = 'pendingInviteToken';

export const getPendingInviteToken = () => sessionStorage.getItem(PENDING_INVITE_KEY);
export const setPendingInviteToken = (token) => sessionStorage.setItem(PENDING_INVITE_KEY, token);
export const clearPendingInviteToken = () => sessionStorage.removeItem(PENDING_INVITE_KEY);

const ROLE_LABELS = { owner: 'Owner', admin: 'Org Admin', member: 'Member' };

/**
 * Public invite-link landing page (Slack-style "you've been invited to X").
 * - Not signed in: shows the org and a "Continue with Google" CTA; the token
 *   is stashed in sessionStorage, threaded through OAuth, and the account is
 *   auto-provisioned server-side.
 * - Signed in: "Accept invitation" joins immediately (idempotent).
 */
const InviteAccept = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { isAuthenticated, acceptInvite, refreshOrganizations } = useAuth();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This invite link is missing its token. Check the URL and try again.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    authAPI
      .inviteInfo(token)
      .then((r) => {
        if (!cancelled) setInfo(r.data);
      })
      .catch(() => {
        if (!cancelled) setInfo({ valid: false });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleGoogle = async () => {
    if (!token) return;
    setError('');
    setGoogleLoading(true);
    try {
      setPendingInviteToken(token);
      const r = await authAPI.getGoogleLoginUrl(token);
      window.location.href = r.data.auth_url;
    } catch (err) {
      clearPendingInviteToken();
      setError(err.response?.data?.detail || 'Google sign-in is unavailable right now.');
      setGoogleLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!token) return;
    setAccepting(true);
    setError('');
    const res = await acceptInvite(token);
    setAccepting(false);
    if (res.success) {
      // Fresh membership list → OrgPicker auto-selects when there's one org.
      await refreshOrganizations();
      navigate('/org-picker', { replace: true });
    } else {
      setError(res.error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f4f2ec' }}>
        <CircularProgress size={28} sx={{ color: '#2a52be' }} />
      </Box>
    );
  }

  const invalid = !info || !info.valid;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f2ec', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ backgroundColor: '#00075e', py: 1.5, px: 4 }}>
        <Typography sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>
          Berkeley Technology &amp; Justice Lab
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', py: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e', mb: 1 }}>
              {invalid ? 'Invitation unavailable' : 'You’ve been invited'}
            </Typography>
            <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996' }}>
              {invalid
                ? 'This invite link is invalid, expired, or has already been used.'
                : `Join ${info.organization_name} on PRA Machine`}
            </Typography>
          </Box>

          <Paper elevation={2} sx={{ p: 4, borderRadius: 2, backgroundColor: '#ffffff', border: '1px solid #e0ddd6' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {invalid ? (
              <Box sx={{ textAlign: 'center' }}>
                <Alert severity={info?.already_redeemed ? 'info' : 'warning'} sx={{ mb: 3 }}>
                  {info?.already_redeemed
                    ? 'This invite has already been accepted.'
                    : 'Ask the organization owner to send you a new invite link.'}
                </Alert>
                {info?.already_redeemed && isAuthenticated && (
                  <Button variant="contained" onClick={() => navigate('/org-picker')} sx={{ textTransform: 'none', backgroundColor: '#2a52be' }}>
                    Go to workspaces
                  </Button>
                )}
              </Box>
            ) : (
              <Box>
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e' }}>
                    {info.organization_name}
                  </Typography>
                  {info.organization_description && (
                    <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996', fontSize: '0.85rem', mt: 0.5 }}>
                      {info.organization_description}
                    </Typography>
                  )}
                  <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996', fontSize: '0.8rem', mt: 1 }}>
                    You'll join as <strong>{ROLE_LABELS[info.role] || 'Member'}</strong>
                  </Typography>
                </Box>

                {!isAuthenticated ? (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={googleLoading ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
                    disabled={googleLoading}
                    onClick={handleGoogle}
                    sx={{ py: 1.25, backgroundColor: '#2a52be', fontWeight: 600, textTransform: 'none', '&:hover': { backgroundColor: '#203f99' } }}
                  >
                    {googleLoading ? 'Redirecting…' : 'Continue with Google to accept'}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={accepting}
                    onClick={handleAccept}
                    sx={{ py: 1.25, backgroundColor: '#2a52be', fontWeight: 600, textTransform: 'none', '&:hover': { backgroundColor: '#203f99' } }}
                  >
                    {accepting ? 'Joining…' : `Accept invitation to ${info.organization_name}`}
                  </Button>
                )}

                <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                  {isAuthenticated ? (
                    <Button variant="text" onClick={() => navigate('/')} sx={{ textTransform: 'none', color: '#838996', fontSize: '0.8rem' }}>
                      Not now — go back to the app
                    </Button>
                  ) : (
                    <Typography sx={{ fontFamily: '"Helvetica Neue", Arial, sans-serif', color: '#838996', fontSize: '0.75rem' }}>
                      Signing in with Google will create your account and add you to this organization.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default InviteAccept;
